import * as amplitude from '@amplitude/analytics-browser';
import { Identify, identify as identifyUser } from '@amplitude/analytics-browser';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';
import { env } from '../config/env';

let isInitialized = false;

/**
 * Initialize Amplitude SDK with API key from environment
 */
export const init = () => {
  if (isInitialized) {
    return;
  }

  if (!env.AMPLITUDE_API_KEY) {
    console.warn('Amplitude API key not found. Amplitude tracking will be disabled.');
    return;
  }

  // Add Session Replay Plugin
  amplitude.add(
    sessionReplayPlugin({
      sampleRate: 0.5, // 1 = 100% sessions, 0.1 = 10%
      privacyConfig: {
        maskAllInputs: false,
        maskAllText: false,
      },
    })
  );

  // Configure default tracking
  // We manually track page views since we don't use routing
  // But keep sessions, file downloads, and form interactions enabled
  amplitude.init(env.AMPLITUDE_API_KEY, {
    defaultTracking: {
      pageViews: false, // We track page views manually
      sessions: true,
      fileDownloads: true,
      formInteractions: true,
    },
  });
  isInitialized = true;
};

/**
 * Set user ID for Amplitude tracking
 * @param email - User email address to use as identifier
 */
export const setUserId = (email: string) => {
  if (!isInitialized) {
    console.warn('Amplitude not initialized. Call init() first.');
    return;
  }

  amplitude.setUserId(email);
};

/**
 * Track a custom event
 * @param eventName - Name of the event to track
 * @param properties - Optional event properties
 */
export const track = (eventName: string, properties?: Record<string, unknown>) => {
  if (!isInitialized) {
    console.warn('Amplitude not initialized. Call init() first.');
    return;
  }

  amplitude.track(eventName, properties);
};

/**
 * Reset user identity (call on logout)
 */
export const reset = () => {
  if (!isInitialized) {
    return;
  }

  amplitude.reset();
};

/**
 * Set user properties
 * @param userProperties - User properties to set
 */
export const identify = (userProperties: Record<string, unknown>) => {
  if (!isInitialized) {
    console.warn('Amplitude not initialized. Call init() first.');
    return;
  }

  const identifyObj = new Identify();
  Object.entries(userProperties).forEach(([key, value]) => {
    identifyObj.set(key, value);
  });
  identifyUser(identifyObj);
};
